// ── AccountsPage ──
import { Fragment, useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { exportCSV, fmt, localFilter } from "../../../shared/format";
import { Badge, Btn, Card, ErrorMsg, Icon, PageLink, RiskBadge, STh, SearchBar, SectionHeader, Spinner, Td, TdNum, Th, ThNum } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { hasFundedTerm } from "../../../shared/accountStatus";
import { useApp } from "../../../shared/appContext";

export const AccountsPage = () => {
    const { api, role, view, go, rowNav, setModal, setConfirm, delRec } = useApp();
    const [rows,setRows]=useState([]); const [loading,setLoading]=useState(true); const [err,setErr]=useState(null);
    const [q,setQ]=useState("");
    const [terms,setTerms]=useState([]);
    const load=()=>{setLoading(true);api.get("getAccounts").then(d=>{setRows(d);setLoading(false);}).catch(e=>{setErr(e.message);setLoading(false);});
      api.get("getTerms").then(d=>setTerms(Array.isArray(d)?d:[])).catch(()=>setTerms([]));};
    useEffect(()=>{load();},[]);

    const filtered = localFilter(rows,q,["companyname","companyemail","accountstatus","industryname","accountnumber","state"])
      .filter(hasFundedTerm);

    const { sorted:displayed, sortKey, sortDir, toggle } = useSortable(filtered, 'companyname');

    const groupOrder = ["Accrual","Non Accrual","Paid Off"];
    const groups = groupOrder.map(status => ({
      status,
      rows: displayed.filter(a => a.accountstatus === status)
    })).filter(g => g.rows.length > 0);

    const totals = arr => ({
      count:      arr.length,
      commitment: arr.reduce((s,a)=>s+(parseFloat(a.accountcommitmentamt)||0),0),
      collateral: arr.reduce((s,a)=>s+(parseFloat(a.totalcollateralvalue)||0),0),
      bod:        arr.reduce((s,a)=>s+(parseFloat(a.accountbalancebod)||0),0),
      eod:        arr.reduce((s,a)=>s+(parseFloat(a.accountbalanceeod)||0),0),
      aeq:        arr.reduce((s,a)=>s+(parseFloat(a.bsloanbalanceeod)||0),0),
      excessavail:arr.reduce((s,a)=>s+(parseFloat(a.excessavailability)||0),0),
    });
    const grandExcludingPaidOff = displayed.filter(a => a.accountstatus !== "Paid Off");
    const grand = totals(grandExcludingPaidOff);
    const COLS = 20; // total number of columns including actions

    // ── Funding in Progress worklist — term-level detail (account fields are always zero here).
    //    Accounts with no loan/term yet still appear so they're reachable right after creation. ──
    const fipAccounts = localFilter(rows,q,["companyname","accountnumber","state","industryname"])
      .filter(a => a.accountstatus === "Funding in Progress");
    const fipTermsByAccount = terms.reduce((m,t)=>{ (m[t.accountid]=m[t.accountid]||[]).push(t); return m; },{});
    // A row here is one of TWO things, and the actions on it have to know which.
    // Most rows are a TERM — the thing being negotiated toward funding. But an
    // account created minutes ago has no loan and no term yet, and it still has
    // to appear or it would be unreachable; that row is the ACCOUNT itself.
    // `kind` carries the difference so the pencil edits, and the bin deletes,
    // the record the row actually stands for rather than whichever one is
    // easier to reach from here.
    // Account #, state and industry come off the ACCOUNT on every row, term or
    // not — a term has no such fields, and the two rows of a two-facility
    // borrower should read identically in those columns.
    const fipAccountCols = a => ({
      accountnumber: a.accountnumber, state: a.state, industryname: a.industryname,
    });
    const fipRows = fipAccounts.flatMap(a=>{
      const ts = fipTermsByAccount[a.id]||[];
      return ts.length
        ? ts.map(t=>({ key:"t"+t.id, kind:"term", rec:t, accountid:a.id, companyname:a.companyname, ...fipAccountCols(a), collateraltype:t.collateraltype, facility_type:t.facility_type, commitment:t.commitment, fundingamount:t.fundingamount }))
        : [{ key:"a"+a.id, kind:"account", rec:a, accountid:a.id, companyname:a.companyname, ...fipAccountCols(a), collateraltype:null, facility_type:null, commitment:0, fundingamount:0 }];
    });

    // The three icons, for whichever kind the row is. The eye opens the SAME
    // record the pencil edits and the bin deletes — on a term row that is the
    // terms sheet, not the account. The company name in the first cell is still
    // the way to the account page, so both destinations stay reachable.
    const fipActions = (t) => t.kind === "term" ? {
      view:  () => setModal({ type:"terms-view", rec:{ ...t.rec, id:t.rec.id, loanid:t.rec.loanid } }),
      edit:  () => setModal({ type:"terms-edit", rec:{ ...t.rec, id:t.rec.id, loanid:t.rec.loanid }, onDone:load }),
      del:   () => setConfirm({
               msg: 'Delete these terms for "'+t.companyname+'"?',
               onConfirm: () => delRec("Terms", t.rec.id, "Deleted terms for "+t.companyname, load) }),
      viewTitle: "View terms", editTitle: "Edit terms", delTitle: "Delete these terms",
    } : {
      view:  () => go("account", { accountid:t.accountid, companyname:t.companyname }),
      edit:  () => setModal({ type:"account-edit", rec:t.rec, onDone:load }),
      del:   () => setConfirm({
               msg: 'Delete "'+t.companyname+'"?',
               onConfirm: () => delRec("Accounts", t.rec.id, "Deleted account "+t.companyname, load) }),
      viewTitle: "Open account", editTitle: "Edit account", delTitle: "Delete this account",
    };
    // ACCOUNT-LEVEL DELETE, FROM THIS CARD.
    //
    // The rows stay term-level, because until a loan funds there is no
    // commitment or funding amount to show at account level — the detail only
    // exists on the term. But the accounts list below EXCLUDES "Funding in
    // Progress", so for an account that has terms there was no route to the
    // account itself anywhere on this page.
    //
    // A second bin beside the first would be the obvious thing and the wrong
    // one: Icon only has three types, so the two would be pixel-identical and
    // the dangerous pair would differ only by tooltip. A labelled button says
    // which scope it acts on.
    //
    // Once per account, not once per row — an account with two terms would
    // otherwise offer to delete itself twice.
    const fipFirstRowOfAccount = {};
    for (const t of fipRows) if (!(t.accountid in fipFirstRowOfAccount)) fipFirstRowOfAccount[t.accountid] = t.key;
    const fipShowAccountDelete = (t) => t.kind === "term" && fipFirstRowOfAccount[t.accountid] === t.key;
    const fipDeleteAccount = (t) => setConfirm({
      msg: 'Delete "'+t.companyname+'"? This removes the account, not just these terms.',
      onConfirm: () => delRec("Accounts", t.accountid, "Deleted account "+t.companyname, load) });

    const fipCommitmentTotal = fipRows.reduce((s,t)=>s+(parseFloat(t.commitment)||0),0);
    const fipFundingTotal    = fipRows.reduce((s,t)=>s+(parseFloat(t.fundingamount)||0),0);

    if(loading) return <Spinner />; if(err) return <ErrorMsg msg={err} onRetry={load} />;

    return <>
      <SectionHeader actions={[
        <SearchBar key="s" value={q} onChange={setQ} />,
        <Btn key="ex" onClick={()=>
          exportCSV(displayed,["accountnumber","companyname","accountstatus","avgriskrating","state","industryname","website","accountcommitmentamt","accountbalancebod","accountbalanceeod","bsloanbalanceeod"],"accounts.csv")}>Export CSV</Btn>,
        role.canEdit&&<Btn key="a" onClick={()=>setModal({type:"account-add",onDone:load})} variant="primary">+ Add Account</Btn>
      ].filter(Boolean)} />

      {/* ── Summary Cards ── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:12, marginBottom:20 }}>
        {/* Grand Total */}
        <Card style={{ padding:14 }}>
          <div style={{ fontSize:11, fontWeight:700, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10, paddingBottom:6, borderBottom:"1.5px solid "+B.cyanLight }}>
            Active Portfolio Total — {grand.count} Accounts
          </div>
          {[
            ["Commitment",       fmt(grand.commitment)],
            ["Collateral",       fmt(grand.collateral)],
            ["Loan Balance", fmt(grand.eod)],
            ["BS Loan Balance",  fmt(grand.aeq)],
            ["Excess Avail",     fmt(grand.excessavail)],
          ].map(([label,value])=>(
            <div key={label} style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:5 }}>
              <span style={{ color:"var(--color-text-secondary)" }}>{label}</span>
              <span style={{ fontWeight:600 }}>{value}</span>
            </div>
          ))}
        </Card>

        {/* Per-group summary cards */}
        {["Accrual","Non Accrual"].map(status => {
          const grpRows = displayed.filter(a => a.accountstatus === status);
          if (grpRows.length === 0) return null;
          const grpTotals = totals(grpRows);
          return (
            <Card key={status} style={{ padding:14 }}>
              <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10, paddingBottom:6, borderBottom:"1.5px solid "+B.cyanLight, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <Badge s={status} />
                <span style={{ fontSize:11, color:"var(--color-text-secondary)", fontWeight:400 }}>{grpTotals.count} account{grpTotals.count!==1?"s":""}</span>
              </div>
              {[
                ["Commitment",       fmt(grpTotals.commitment)],
                ["Collateral",       fmt(grpTotals.collateral)],
                ["Loan Balance", fmt(grpTotals.eod)],
                ["BS Loan Balance",  fmt(grpTotals.aeq)],
                ["Excess Avail",     fmt(grpTotals.excessavail)],
              ].map(([label,value])=>(
                <div key={label} style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:5 }}>
                  <span style={{ color:"var(--color-text-secondary)" }}>{label}</span>
                  <span style={{ fontWeight:600 }}>{value}</span>
                </div>
              ))}
            </Card>
          );
        })}
      </div>

      {/* ── Funding in Progress worklist — shows only when there are accounts in funding ── */}
      {fipRows.length > 0 && (
        <Card style={{ overflowX:"auto", marginBottom:20, borderLeft:"3px solid "+B.cyan }}>
          <div style={{ fontSize:11, fontWeight:700, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", padding:"12px 12px 8px" }}>
            Funding in Progress — {fipRows.length} {fipRows.length===1?"item":"items"}
          </div>
          <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
            {/* Same columns, order and cell treatment as the accounts table
                below, minus the five that carry nothing here: every row is
                "Funding in Progress" by definition, no balance exists until a
                loan funds, and a risk rating is not set until after close. */}
            <thead><tr>
              <Th>Account #</Th>
              <Th>Company Name</Th>
              <Th>Coll Type</Th>
              <Th>Facility Type</Th>
              <Th>State</Th>
              <Th>Industry</Th>
              <ThNum>Commitment Amt</ThNum>
              <ThNum>Funding Amount</ThNum>
              <Th></Th>
            </tr></thead>
            <tbody>
              {fipRows.length===0
                ? <tr><td colSpan={9} style={{ padding:16, textAlign:"center", color:"var(--color-text-secondary)", fontSize:13 }}>No accounts in funding.</td></tr>
                : fipRows.map(t=>{
                    const act = fipActions(t);
                    return (
                    <tr key={t.key} style={{ cursor:"pointer" }}
                      {...rowNav("account",{accountid:t.accountid,companyname:t.companyname})}
                      onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"}
                      onMouseLeave={e=>e.currentTarget.style.background=""}>
                      <Td><PageLink page="account" params={{accountid:t.accountid,companyname:t.companyname}} style={{ fontFamily:"var(--font-mono)",fontSize:12 }}>{t.accountnumber||"—"}</PageLink></Td>
                      <Td style={{ fontWeight:500, maxWidth:200, overflow:"hidden", textOverflow:"ellipsis" }}>
                        <PageLink page="account" params={{accountid:t.accountid,companyname:t.companyname}} title={t.companyname}>{t.companyname}</PageLink>
                      </Td>
                      <Td>{t.collateraltype||"—"}</Td>
                      <Td style={{ maxWidth:160, overflow:"hidden", textOverflow:"ellipsis" }}>
                        <span title={t.facility_type||""}>{t.facility_type||"—"}</span>
                      </Td>
                      <Td>{t.state||"—"}</Td>
                      <Td style={{ maxWidth:160, overflow:"hidden", textOverflow:"ellipsis" }}>
                        <span title={t.industryname||""}>{t.industryname||"—"}</span>
                      </Td>
                      <TdNum>{fmt(t.commitment||0)}</TdNum>
                      <TdNum>{fmt(t.fundingamount||0)}</TdNum>
                      {/* stopPropagation, or every icon also navigates: the whole
                          row carries rowNav.
                          flex-end, because this table has fewer columns than the
                          one below and its last cell is therefore wide — without
                          it the icons sit at the left of that space instead of
                          on the right edge, where the ones below them are. */}
                      <Td><div style={{ display:"flex", gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
                        <Btn onClick={act.view} title={act.viewTitle}><Icon type="view" /></Btn>
                        {role.canEdit&&<Btn onClick={act.edit} title={act.editTitle}><Icon type="edit" /></Btn>}
                        {role.canDelete&&<Btn onClick={act.del} title={act.delTitle} variant="danger"><Icon type="delete" /></Btn>}
                        {role.canDelete&&fipShowAccountDelete(t)&&
                          <Btn onClick={()=>fipDeleteAccount(t)} variant="danger"
                               title={"Delete the account "+t.companyname+", not just these terms"}>Delete account</Btn>}
                      </div></Td>
                    </tr>
                    );
                  })}
              {fipRows.length>0 && (
                <tr style={{ background:"var(--color-background-secondary)", borderTop:"1.5px solid "+B.cyanLight }}>
                  <td colSpan={6} style={{ padding:"8px 12px", fontSize:12, fontWeight:600, color:B.cyanDark }}>Total ({fipRows.length})</td>
                  <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(fipCommitmentTotal)}</TdNum>
                  <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(fipFundingTotal)}</TdNum>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      )}

      <Card style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
          <thead><tr>
            <STh label="Account #"       sortKey="accountnumber"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Company Name"         sortKey="companyname"         currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Coll Type"          sortKey="collateraltypes"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Facility Type"          sortKey="facilitytypes"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />          
            <STh label="Status"          sortKey="accountstatus"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="RRating"          sortKey="avgriskrating"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />           
            <STh label="State"           sortKey="state"               currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Industry"        sortKey="industryname"        currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
            <STh label="Commitment Amt"  sortKey="accountcommitmentamt" currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
            <STh label="Collateral"  sortKey="totalcollateralvalue"   currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
            <STh label="Loan Balance" sortKey="accountbalanceeod"  currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
            <STh label="BS Loan Balance" sortKey="bsloanbalanceeod" currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
            <STh label="Excess Avail" sortKey="excessavailability" currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
            <Th></Th>
          </tr></thead>
          <tbody>
            {groups.map(group => {
              const sub = totals(group.rows);
              // A bare <> cannot carry a key, so every status band was an
              // unkeyed sibling in the same list — React warns, and a reorder
              // can reuse the wrong rows' state.
              return <Fragment key={"grp-"+group.status}>
                {/* ── Group header ── */}
                <tr key={"hdr-"+group.status}>
                  <td colSpan={COLS} style={{ padding:"8px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:B.cyanDark, background:B.cyanLight }}>
                    <Badge s={group.status} />
                    <span style={{ marginLeft:8, color:B.cyanDark, fontSize:11 }}>{sub.count} account{sub.count!==1?"s":""}</span>
                  </td>
                </tr>

                {/* ── Group rows ── */}
                {group.rows.map(a=>(
                  <tr key={a.id} style={{ cursor:"pointer" }} {...rowNav("account",{accountid:a.id,companyname:a.companyname})} onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                    <Td><PageLink page="account" params={{accountid:a.id,companyname:a.companyname}} style={{ fontFamily:"var(--font-mono)",fontSize:12 }}>{a.accountnumber||"—"}</PageLink></Td>
                    <Td style={{ fontWeight:500, maxWidth:200, overflow:"hidden", textOverflow:"ellipsis" }}>
                      <PageLink page="account" params={{accountid:a.id,companyname:a.companyname}} title={a.companyname}>{a.companyname}</PageLink>
                    </Td>
                    <Td>{a.collateraltypes}</Td>
                    <Td style={{ maxWidth:160, overflow:"hidden", textOverflow:"ellipsis" }}>
                     <span title={a.facilitytypes||""}>{a.facilitytypes||"—"}</span>
                    </Td>

                    <Td><Badge s={a.accountstatus} /></Td>
                    <TdNum><RiskBadge rating={a.avgriskrating ? Number(a.avgriskrating).toFixed(1) : null} /></TdNum>
                    <Td>{a.state||"—"}</Td>
                    <Td style={{ maxWidth:160, overflow:"hidden", textOverflow:"ellipsis" }}>
                      <span title={a.industryname||""}>{a.industryname||"—"}</span>
                    </Td>
                    <TdNum>{fmt(a.accountcommitmentamt||0)}</TdNum>
                    <TdNum>{fmt(a.totalcollateralvalue||0)}</TdNum>
                    <TdNum>{fmt(a.accountbalanceeod||0)}</TdNum>
                    <TdNum>{fmt(a.bsloanbalanceeod||0)}</TdNum>
                    <TdNum style={{ color: parseFloat(a.excessavailability||0) >= 0 ? 'inherit' : '#993C1D' }}> {fmt(a.excessavailability||0)}</TdNum>
                    <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
                      <Btn onClick={()=>go("account",{accountid:a.id,companyname:a.companyname})}><Icon type="view" /></Btn>
                      {role.canEdit&&<Btn onClick={()=>setModal({type:"account-edit",rec:a,onDone:load})}><Icon type="edit" /></Btn>}
                      {role.canDelete&&<Btn onClick={()=>setConfirm({msg:'Delete "'+a.companyname+'"?',onConfirm:()=>delRec("Accounts",a.id,"Deleted account "+a.companyname,load)})} variant="danger"><Icon type="delete" /></Btn>}
                    </div></Td>
                  </tr>
                ))}

                {/* ── Group subtotal ── */}
                <tr key={"sub-"+group.status} style={{ background:"var(--color-background-secondary)", borderTop:"1.5px solid "+B.cyanLight }}>
                  <td colSpan={8} style={{ padding:"8px 12px", fontSize:12, fontWeight:600, color:B.cyanDark, textAlign:"right" }}>
                    {group.status} Subtotal ({sub.count})
                  </td>
                  <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(sub.commitment)}</TdNum>
                  <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(sub.collateral)}</TdNum>
                  <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(sub.eod)}</TdNum>
                  <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(sub.aeq)}</TdNum>
                  <TdNum style={{ fontWeight:600, color:B.cyanDark }}>{fmt(sub.excessavail)}</TdNum>
                  <td />
                </tr>
              </Fragment>;
            })}

            {/* ── Grand total row ── */}
            <tr style={{ background:B.cyanLight, borderTop:"2px solid "+B.cyan }}>
              <td colSpan={8} style={{ padding:"10px 12px", fontSize:13, fontWeight:700, color:B.cyanDark, textAlign:"right" }}>
                Grand Total ({grand.count} accounts)
              </td>
              <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grand.commitment)}</TdNum>
              <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grand.collateral)}</TdNum>
              <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grand.eod)}</TdNum>
              <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grand.aeq)}</TdNum>
              <TdNum style={{ fontWeight:700, color:B.cyanDark }}>{fmt(grand.excessavail)}</TdNum>
           
              <td />
            </tr>
          </tbody>
        </table>
      </Card>
    </>;
  };
